import React, { useEffect, useRef, useState } from "react";
import { useSession } from "@clerk/react";
import { getAudioSecureUrl } from "@/api/audio";

export default function MeetingAudioPlayer({ meeting_id }) {
  const { session } = useSession();
  const audioRef = useRef(null);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setUrl("");
    setError("");
    if (!meeting_id || !session) return;
    setLoading(true);
    (async () => {
      try {
        const token = await session.getToken();
        const { data } = await getAudioSecureUrl(meeting_id, token);
        if (!data.url) throw new Error("Missing playback URL");
        if (!cancelled) setUrl(data.url);
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 404
          ? "This recording is still processing. Try again shortly."
          : "Unable to load this recording. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [meeting_id, session, retry]);

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      }
    };
  }, [url]);

  return (
    <div className="rounded-xl bg-background/60 p-4 w-full" aria-busy={loading}>
      {loading && <p role="status">Loading recording…</p>}
      {url && <audio key={url} ref={audioRef} src={url} controls playsInline preload="metadata"
        className="w-full" aria-label="Meeting recording"
        onError={() => setError("Playback was interrupted. Retry to refresh the recording.")} />}
      {!meeting_id && <p>No audio available for this meeting yet.</p>}
      {error && <div role="alert" className="mt-2 text-sm text-danger">
        <p>{error}</p>
        <button type="button" className="mt-2 underline" onClick={() => setRetry(value => value + 1)}>Retry recording</button>
      </div>}
    </div>
  );
}
